import EmblaCarousel from './custom.embla-carousel.esm.js';
// Source: KR cms-photo-autoplay.ts canonical defaults; scoped photo navigation only.
const PHOTO_AUTOPLAY_MS={min:1000,max:60000,default:4000};
const PHOTO_TRANSITION_SPEED={default:3};
const PHOTO_TRANSITION_DURATION={1:60,2:40,3:25,4:18,5:12};
const PHOTO_CONTINUOUS_PX_PER_SECOND={1:12,2:24,3:40,4:64,5:96};

const mobilePhotoOrders = new WeakMap();

export function photoBuildOrder(ids,title,playback={},fallbackRoot, mobile=matchMedia("(max-width: 767px)").matches) {
  const cacheRoot = playback.root || fallbackRoot;
  let orders = mobilePhotoOrders.get(cacheRoot);
  if (!orders) { orders = new Map(); mobilePhotoOrders.set(cacheRoot, orders); }
  const orderKey = (playback.shuffleKey || title) + ':' + JSON.stringify(ids);
  const cachedOrder = orders.get(orderKey);
  if (cachedOrder && (!Array.isArray(cachedOrder) || cachedOrder.length !== ids.length || new Set(cachedOrder).size !== ids.length || cachedOrder.some(index => !Number.isInteger(index) || index < 0 || index >= ids.length))) orders.delete(orderKey);
  if (!playback.random) orders.delete(orderKey);
  if (playback.random && !playback.editable && !orders.has(orderKey)) {
    const order = ids.map((_, index) => index);
    for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
    orders.set(orderKey, order);
  }
  return mobile && playback.random && !playback.editable ? [...(orders.get(orderKey) || ids.map((_,i)=>i))] : ids.map((_,i)=>i);
}


// @policy: UI_POLICY — Embla 8.6 keeps canonical media and editing targets intact.
export function photoRail(wrap, track, slides, title, playback = {}) {
  const viewport = document.createElement('div');
  viewport.className = 'hv-carousel-viewport';
  track.classList.add('hv-photo-rail');
  track.replaceWith(viewport);
  viewport.append(track);
  const controls = document.createElement('div');
  controls.className = 'hv-carousel-controls';
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'hv-carousel-slider';
  slider.min = '0'; slider.max = '1000'; slider.step = '1'; slider.value = '0';
  const labels = wrap.closest('[data-photo-position]')?.dataset || {};
  slider.setAttribute('aria-label', (labels.photoPosition || '__title__').replace('__title__', title));
  const status = document.createElement('span');
  status.className = 'hv-carousel-announcement';
  status.setAttribute('aria-atomic', 'true');
  const events = new AbortController();
  controls.append(slider, status); wrap.append(controls);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 767px)');
  const active = () => playback.mobileOnly ? mobile.matches && !playback.mobileGrid : !mobile.matches || !playback.mobileGrid;
  // Keep a visitor's chosen order stable across the same feed's redraws and resize.
  const originalSlides = [...slides];
  let orderedOriginalSlides = [...slides], adjustingLoop = false;
  const applyOrder = () => {
    orderedOriginalSlides = mobile.matches && playback.random && !playback.editable ? photoBuildOrder(playback.slideIds || originalSlides.map((slide,index)=>slide.dataset.hvItemId || index),title,playback,wrap,true).map(index => originalSlides[index]) : [...originalSlides];
    slides = [...orderedOriginalSlides];
    track.replaceChildren(...slides);
  };
  applyOrder();
  const configured = playback.enabled === true;
  const dwell = Math.max(PHOTO_AUTOPLAY_MS.min, Math.min(PHOTO_AUTOPLAY_MS.max, Number(playback.dwell) || PHOTO_AUTOPLAY_MS.default));
  const duration = PHOTO_TRANSITION_DURATION[Number(playback.speed ?? PHOTO_TRANSITION_SPEED.default)] ?? PHOTO_TRANSITION_DURATION[PHOTO_TRANSITION_SPEED.default];
  const continuous = playback.mode === 'continuous';
  const velocity = PHOTO_CONTINUOUS_PX_PER_SECOND[Number(playback.speed ?? PHOTO_TRANSITION_SPEED.default)] ?? PHOTO_CONTINUOUS_PX_PER_SECOND[PHOTO_TRANSITION_SPEED.default];
  let api, initFrame, timer, flowFrame, lastFrame = null, lastSync = 0, live = true, moving = false, overflow = false, onScreen = false, hovering = false;
  let requested = configured && !playback.editable && !reduced.matches;
  const stopTimer = () => { clearTimeout(timer); timer = null; cancelAnimationFrame(flowFrame); flowFrame = null; lastFrame = null; };
  const canPlay = () => live && active() && requested && !playback.editable && !reduced.matches && overflow && onScreen && !document.hidden && wrap.isConnected && !hovering;
  const syncPlayback = () => {
    wrap.dataset.hvAutoplay = canPlay() ? 'playing' : 'paused';
    status.setAttribute('aria-live', configured && requested ? 'off' : 'polite');
  };
  // Embla 8.6 engine adapter: advance the canonical Embla slides and loop vectors.
  const flow = now => {
    flowFrame = null;
    if (!api || !canPlay()) { lastFrame = null; return; }
    const engine = api.internalEngine();
    const elapsed = lastFrame === null ? 0 : Math.min(now - lastFrame, 50);
    lastFrame = now;
    engine.animation.stop();
    let next = engine.location.get() - velocity * elapsed / 1000;
    if (!engine.options.loop && next <= engine.limit.min) next = engine.limit.max;
    [engine.location, engine.offsetLocation, engine.previousLocation, engine.target].forEach(vector => vector.set(next));
    if (engine.options.loop) { engine.scrollLooper.loop(-1); engine.slideLooper.loop(); }
    engine.translate.to(engine.offsetLocation.get());
    if (now - lastSync >= 100) { sync(); lastSync = now; }
    flowFrame = requestAnimationFrame(flow);
  };
  const schedule = () => {
    stopTimer(); syncPlayback();
    if (!api || !canPlay()) return;
    if (continuous) { flowFrame = requestAnimationFrame(flow); return; }
    if (moving) return;
    timer = setTimeout(() => {
      timer = null;
      if (!canPlay()) return;
      moving = true;
      api.canScrollNext() ? api.scrollNext() : api.scrollTo(0);
    }, dwell);
  };
  const pause = () => {
    requested = false; stopTimer();
    if (api && continuous) {
      const engine = api.internalEngine();
      engine.index.set(engine.scrollTarget.byDistance(0, false).index);
    }
    syncPlayback();
  };
  const sync = () => {
    if (!api) return;
    overflow = active() && api.scrollSnapList().length > 1;
    controls.hidden = !overflow;
    track.tabIndex = overflow ? 0 : -1;
    if (!overflow) stopTimer();
    // Public snap indices stay stable when Embla wraps its internal loop vectors.
    // Raw location modulo cycle can turn a tiny negative rounding error at Home
    // into an entire cycle and incorrectly report range=1000 at the first photo.
    const count = Math.min(originalSlides.length, api.scrollSnapList().length);
    let first = ((api.selectedScrollSnap() % originalSlides.length) + originalSlides.length) % originalSlides.length;
    if (continuous && requested) {
      // Continuous autoplay advances engine vectors directly, so derive its
      // current photo from rendered geometry instead of a stale selected index.
      const left = viewport.getBoundingClientRect().left;
      const nearest = slides.map((slide,index) => ({index, distance:Math.abs(slide.getBoundingClientRect().left-left)}))
        .sort((a,b)=>a.distance-b.distance)[0];
      first = (nearest?.index ?? 0) % originalSlides.length;
    }
    slider.value = String(Math.round((count > 1 ? Math.min(first,count-1)/(count-1) : 0)*1000));
    const announcement = (labels.photoCount || '__current__ / __total__').replace('__current__', String(first + 1)).replace('__total__', String(originalSlides.length));
    slider.setAttribute('aria-valuetext', announcement);
    status.textContent = announcement;
  };
  const announce = () => { sync(); };
  // Embla measures its loop's final spacing from the last slide's margin,
  // whereas CSS grid's column-gap exists only between tracks. Keep the final
  // card at its full grid-track width while exposing that same gap to Embla.
  let loopEndSlide, loopEndStyles;
  const syncLoopEndGap = () => {
    if (loopEndSlide) {
      loopEndSlide.style.marginRight = loopEndStyles.marginRight;
      loopEndSlide.style.width = loopEndStyles.width;
      loopEndSlide = null;
    }
    if (!active() || !slides.length) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    if (!gap) return;
    // A fully fitted PC row is not a loop: an end margin would create a
    // scroll snap and a progress bar where the original layout had neither.
    const contentWidth = slides.reduce((sum, slide) => sum + slide.getBoundingClientRect().width, 0) + gap * (slides.length - 1);
    if (contentWidth <= viewport.getBoundingClientRect().width + 1) return;
    loopEndSlide = slides[slides.length - 1];
    loopEndStyles = { marginRight: loopEndSlide.style.marginRight, width: loopEndSlide.style.width };
    loopEndSlide.style.width = '100%';
    loopEndSlide.style.marginRight = gap + 'px';
  };
  const options = () => {
    syncLoopEndGap();
    return {
      watchResize: () => { syncLoopEndGap(); return true; },
      active: active(), align: 'start', containScroll: 'trimSnaps', loop: true,
      dragFree: !reduced.matches, duration: reduced.matches ? 0 : duration,
      watchDrag: (_api, event) => !event.ctrlKey && !event.metaKey && !event.target.closest?.('input,textarea,select,button,[contenteditable="true"],video,iframe'),
    };
  };
  const ensureLoop = () => {
    if (!api || adjustingLoop) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const originalWidth = orderedOriginalSlides.reduce((sum, slide) => sum + slide.getBoundingClientRect().width, 0) + gap * (originalSlides.length - 1);
    const needsOverflow = active() && originalWidth > viewport.getBoundingClientRect().width + 1;
    const repeat = needsOverflow && !api.internalEngine().options.loop && playback.repeats?.length === originalSlides.length;
    const remove = slides.length > originalSlides.length && !needsOverflow;
    if (!repeat && !remove) return;
    adjustingLoop = true;
    slides = repeat ? [...orderedOriginalSlides, ...orderedOriginalSlides.map(slide => playback.repeats[originalSlides.indexOf(slide)])] : [...orderedOriginalSlides];
    track.replaceChildren(...slides); api.reInit(options()); adjustingLoop = false;
  };
  const observer = configured ? new IntersectionObserver(entries => { onScreen = entries.some(entry => entry.isIntersecting); schedule(); }) : null;
  const init = () => {
    if (!live) return;
    if (!wrap.isConnected) { initFrame = requestAnimationFrame(init); return; }
    if (!touchSurfaceBound) {
      const surface = wrap.closest('[data-hv-section]') || wrap;
      for (const type of ['touchstart', 'touchmove', 'touchend', 'touchcancel']) surface.addEventListener(type, forwardTouch, { capture: true, passive: false, signal: events.signal });
      touchSurfaceBound = true;
    }
    api = EmblaCarousel(viewport, options());
    ensureLoop();
    api.on('scroll', () => { moving = true; stopTimer(); sync(); })
      .on('settle', () => { moving = false; announce(); schedule(); })
      .on('reInit', () => { stopTimer(); ensureLoop(); moving = false; announce(); schedule(); })
      .on('pointerDown', pause)
      .on('pointerUp', () => { if (reduced.matches) api.scrollTo(api.selectedScrollSnap(), true); });
    viewport.dataset.hvMotion = 'embla-8.6.0';
    announce(); observer?.observe(wrap); schedule();
  };
  wrap.addEventListener('mouseenter', () => { hovering = true; schedule(); }, { signal: events.signal });
  wrap.addEventListener('mouseleave', () => { hovering = false; schedule(); }, { signal: events.signal });
  // Manual interaction stays paused until the section is revisited or reconfigured.
  viewport.addEventListener('pointerdown', pause, { signal: events.signal });
  viewport.addEventListener('wheel', event => {
    if (!api || controls.hidden || event.defaultPrevented || event.ctrlKey || event.metaKey) return;
    const delta = event.shiftKey ? (event.deltaX || event.deltaY) : Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
    if (!delta) return;
    event.preventDefault(); pause();
    const engine = api.internalEngine(), pixels = delta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientWidth : 1);
    engine.scrollBody.useBaseFriction().useDuration(reduced.matches ? 0 : 25);
    engine.scrollTo.distance(-pixels, false);
    if (reduced.matches) announce();
  }, { passive: false, signal: events.signal });
  // Chromium can retarget a touch in the space between photos to the section.
  // Forward only that viewport hit to Embla; its normal axis test still lets
  // vertical gestures scroll the page and modifiers remain owned by cropping.
  let forwardedTouch = false, touchSurfaceBound = false;
  const forwardTouch = event => {
    if (viewport.contains(event.target)) return;
    if (event.type === 'touchstart') {
      const point = event.touches[0], bounds = viewport.getBoundingClientRect();
      forwardedTouch = !!api && !controls.hidden && !viewport.contains(event.target) && !event.ctrlKey && !event.metaKey && event.touches.length === 1 && !!point && point.clientX >= bounds.left && point.clientX <= bounds.right && point.clientY >= bounds.top && point.clientY <= bounds.bottom;
    }
    if (!forwardedTouch || viewport.contains(event.target)) return;
    const proxy = new TouchEvent(event.type, { bubbles: true, cancelable: event.cancelable, touches: event.touches, targetTouches: event.targetTouches, changedTouches: event.changedTouches, ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey, altKey: event.altKey });
    viewport.dispatchEvent(proxy);
    if (proxy.defaultPrevented) event.preventDefault();
    if (event.type === 'touchend' || event.type === 'touchcancel') forwardedTouch = false;
  };
  wrap.addEventListener('focusin', pause, { signal: events.signal });
  document.addEventListener('visibilitychange', schedule, { signal: events.signal });
  slider.addEventListener('input', () => {
    if (!api) return;
    pause();
    const count = Math.min(originalSlides.length, api.scrollSnapList().length);
    const index = Math.round(Number(slider.value) / 1000 * Math.max(0,count-1));
    // Range input selects an exact canonical photo. Use Embla's public API so
    // target/index/location/loop translations update together for every rail.
    api.scrollTo(index, true);
    announce();
  }, { signal: events.signal });
  wrap.addEventListener('keydown', event => {
    if (!api || controls.hidden || (event.target !== slider && event.target.closest?.('input,textarea,select,button,[contenteditable="true"],[data-cms-frame]'))) return;
    if (['ArrowRight','ArrowLeft','ArrowUp','ArrowDown'].includes(event.key)) {
      pause(); event.preventDefault();
      ['ArrowRight','ArrowUp'].includes(event.key) ? api.scrollNext(reduced.matches) : api.scrollPrev(reduced.matches);
      // Instant reduced-motion jumps can finish without Embla's settle event; announce the visible result now.
      if (reduced.matches) announce();
    } else if (event.key === 'Home' || event.key === 'End') {
      pause(); event.preventDefault(); api.scrollTo(event.key === 'Home' ? 0 : Math.min(originalSlides.length, api.scrollSnapList().length) - 1, true);
      announce();
    }
  }, { signal: events.signal });
  mobile.addEventListener('change', () => { stopTimer(); applyOrder(); api?.reInit(options()); }, { signal: events.signal });
  reduced.addEventListener('change', () => { stopTimer(); if (reduced.matches) pause(); api?.reInit(options()); }, { signal: events.signal });
  initFrame = requestAnimationFrame(init);
  return { node: wrap, dispose: () => { live = false; stopTimer(); cancelAnimationFrame(initFrame); observer?.disconnect(); events.abort(); api?.destroy(); if (loopEndSlide) { loopEndSlide.style.marginRight = loopEndStyles.marginRight; loopEndSlide.style.width = loopEndStyles.width; } } };
}

// Shopify section lifecycle: each rail owns and destroys only its own photo engine.
const selectionInstances = new Map();
function mountSelections(scope=document) {
  for (const root of scope.querySelectorAll('[data-hv-shopify-selection]')) {
    if(selectionInstances.has(root)) continue;
    const wrap=root.querySelector('.hv-carousel'),track=root.querySelector('.hv-selection-track');
    if(!track || !track.children.length) continue;
    const slides=[...track.children];
    const repeats=slides.map(slide=>{const clone=slide.cloneNode(true);clone.setAttribute('aria-hidden','true');for(const link of clone.querySelectorAll('a'))link.tabIndex=-1;return clone;});
    selectionInstances.set(root,photoRail(wrap,track,slides,root.dataset.title||'',{repeats,enabled:false,mobileOnly:root.hasAttribute('data-photo-mobile-only')}));
  }
}
function unmountSelections(scope){for(const [root,instance] of selectionInstances){if(root===scope||scope.contains(root)){instance.dispose();selectionInstances.delete(root);}}}
document.addEventListener('shopify:section:load',event=>mountSelections(event.target));
document.addEventListener('shopify:section:unload',event=>unmountSelections(event.target));
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>mountSelections(),{once:true});else mountSelections();
